import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { applyRenataFieldToForm } from "@/lib/renata/workspace-focus";
import { CheckCircle, XCircle, Loader2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { clientRequestHeaders } from "@/lib/sm-api";
import type { RenataProposal } from "@/lib/renata/types";
import {
  resolveApprovalNextStep,
  type ApprovalNextStep,
} from "@/lib/renata/experience/next-step";
import { ApprovalCenter, type ApprovalCenterAction } from "./ApprovalCenter";

interface RenataProposalCardProps {
  proposal: RenataProposal;
  conversationId: string;
  messageId: string;
  tenantId: string;
  onResult: (proposal: RenataProposal, approved: boolean, result?: unknown) => void;
}

function looksLikeDelete(proposal: RenataProposal): boolean {
  const action = `${proposal.action ?? ""} ${proposal.label ?? ""} ${proposal.description ?? ""}`.toLowerCase();
  if (/delete|eliminar|borrar|remove/.test(action)) return true;
  const params = proposal.params ?? {};
  const blob = JSON.stringify(params).toLowerCase();
  return /"delete"|"eliminar"|"borrar"|soft_?delete|hard_?delete/.test(blob);
}

function extractDiff(proposal: RenataProposal): ApprovalCenterAction["diff"] | undefined {
  const params = proposal.params ?? {};
  const before =
    typeof params.before === "string"
      ? params.before
      : params.before != null
        ? JSON.stringify(params.before, null, 2)
        : undefined;
  const after =
    typeof params.after === "string"
      ? params.after
      : params.after != null
        ? JSON.stringify(params.after, null, 2)
        : undefined;
  if (before == null && after == null) return undefined;
  return { before, after };
}

export function RenataProposalCard({
  proposal,
  conversationId,
  messageId,
  tenantId,
  onResult,
}: RenataProposalCardProps) {
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [finalStatus, setFinalStatus] = useState<"executed" | "cancelled" | null>(null);
  const [approvalOpen, setApprovalOpen] = useState(false);
  const [nextStep, setNextStep] = useState<ApprovalNextStep | null>(null);

  const requiresDoubleConfirm = useMemo(() => looksLikeDelete(proposal), [proposal]);

  const approvalAction = useMemo<ApprovalCenterAction>(
    () => ({
      id: proposal.id,
      title: proposal.label,
      description: proposal.description,
      preview: proposal.preview,
      diff: extractDiff(proposal),
      impact:
        typeof proposal.params?.impact === "string"
          ? proposal.params.impact
          : undefined,
      recordCount:
        typeof proposal.params?.recordCount === "number"
          ? proposal.params.recordCount
          : typeof proposal.params?.count === "number"
            ? proposal.params.count
            : undefined,
      rollbackPossible:
        typeof proposal.params?.rollbackPossible === "boolean"
          ? proposal.params.rollbackPossible
          : undefined,
      skillId: proposal.skill_id,
      action: proposal.action,
      params: proposal.params,
      requiresDoubleConfirm,
    }),
    [proposal, requiresDoubleConfirm],
  );

  const isFieldApply = proposal.skill_id === "inline_field" || proposal.action === "apply_field";

  // Field apply is handled exclusively by RenataQuickActions — avoid duplicate Aplicar/Cancelar.
  if (isFieldApply) {
    return null;
  }

  async function submit(approved: boolean) {
    setLoading(true);
    try {
      // Aplicar texto al formulario abierto — no pasa por Approval API.
      if (isFieldApply) {
        if (approved) {
          const field = String(proposal.params?.field ?? "");
          const value = String(proposal.params?.after ?? proposal.preview ?? "");
          applyRenataFieldToForm({
            field,
            value,
            entityId:
              (proposal.params?.producto_id as string | undefined) ??
              (proposal.params?.entityId as string | undefined) ??
              null,
          });
        }
        const status = approved ? "executed" : "cancelled";
        setFinalStatus(status);
        setDone(true);
        if (approved) {
          setNextStep({
            message: "Listo. Revisá el texto en el campo y guardá si te convence.",
            ctaLabel: "Seguir editando",
            href: typeof window !== "undefined" ? window.location.pathname : "/app",
          });
        }
        onResult({ ...proposal, status }, approved);
        return;
      }

      const res = await fetch("/api/renata/action", {
        method: "POST",
        credentials: "include",
        headers: {
          ...clientRequestHeaders(tenantId),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          conversationId,
          messageId,
          proposalId: proposal.id,
          skillId: proposal.skill_id,
          action: proposal.action,
          params: proposal.params,
          approved,
          doubleConfirmed: approved && requiresDoubleConfirm,
        }),
      });
      if (!res.ok) throw new Error("action failed");
      const data = (await res.json()) as { result?: unknown };
      const status = approved ? "executed" : "cancelled";
      setFinalStatus(status);
      setDone(true);
      onResult({ ...proposal, status }, approved, data.result);
      if (approved) {
        const step = resolveApprovalNextStep({
          skillId: proposal.skill_id,
          action: proposal.action,
          params: proposal.params,
        });
        setNextStep(step);
        toast.success(step.message);
      }
    } catch {
      toast.error("No pudimos completar la acción. Revisá los datos e intentá nuevamente.");
      setDone(true);
      setFinalStatus(approved ? "executed" : "cancelled");
    } finally {
      setLoading(false);
      setApprovalOpen(false);
    }
  }

  if (done) {
    const approved = finalStatus === "executed" || proposal.status === "executed";
    return (
      <div
        className="mt-2 space-y-2 rounded-lg border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground"
        data-testid="renata-approval-next-step"
      >
        {approved ? (
          <>
            <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
              <CheckCircle className="h-3 w-3" /> Aprobado
            </span>
            {nextStep ? (
              <>
                <p className="text-foreground/80">{nextStep.message}</p>
                <Button asChild size="sm" variant="outline" className="h-7 gap-1 text-xs">
                  <Link to={nextStep.href}>
                    {nextStep.ctaLabel}
                    <ArrowRight className="h-3 w-3" />
                  </Link>
                </Button>
              </>
            ) : null}
          </>
        ) : (
          <span className="flex items-center gap-1 text-muted-foreground">
            <XCircle className="h-3 w-3" /> Cancelado
          </span>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="mt-3 rounded-lg border border-primary/20 bg-primary/5 p-3">
        <p className="text-sm font-medium">{proposal.label}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">{proposal.description}</p>
        {typeof proposal.params?._explain_text === "string" && proposal.params._explain_text ? (
          <pre className="mt-2 whitespace-pre-wrap rounded-md bg-muted/40 p-2 text-[11px] text-muted-foreground">
            {String(proposal.params._explain_text)}
          </pre>
        ) : null}
        {proposal.preview && (
          <pre className="mt-2 rounded bg-muted p-2 text-xs overflow-x-auto">{proposal.preview}</pre>
        )}
        
      <div className="rounded-md border border-dashed bg-muted/20 px-3 py-2 text-[11px] text-muted-foreground space-y-1">
        <p><span className="font-medium text-foreground">Qué cambiará:</span> solo lo indicado en esta propuesta.</p>
        <p><span className="font-medium text-foreground">Qué no cambiará:</span> el resto de tu catálogo y configuración.</p>
        <p><span className="font-medium text-foreground">Cómo deshacer:</span> desde el historial de la entidad o pidiendo a Renata que revierta el cambio.</p>
        <p><span className="font-medium text-foreground">Después:</span> verás el resultado aplicado y el siguiente paso sugerido.</p>
      </div>
      <div className="mt-3 flex gap-2">
          <Button
            size="sm"
            onClick={() => {
              if (isFieldApply) void submit(true);
              else setApprovalOpen(true);
            }}
            disabled={loading}
            className="flex-1"
            data-renata-approve
          >
            {loading ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : isFieldApply ? (
              "Aplicar en el campo"
            ) : (
              "Aprobar"
            )}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => void submit(false)}
            disabled={loading}
            className="flex-1"
          >
            Cancelar
          </Button>
        </div>
      </div>

      <ApprovalCenter
        open={approvalOpen}
        action={approvalAction}
        loading={loading}
        onApprove={() => submit(true)}
        onCancel={() => setApprovalOpen(false)}
      />
    </>
  );
}
